import React, { FunctionComponent, useEffect } from 'react';
import {
    CandidateInfoTitle,
    CandidateInfoTitleSection,
    CloseIcon,
    CandidateInfoModalWrapper,
    SectionWrapper,
    DetailWrapper,
    Detail,
    Status,
    CandidateInfoSection,
} from './Styles';
import { CrossIcon } from 'evergreen-ui';
import { getLastCompletedSession } from '../TargetGroupTable';
import {
    formatDate,
    ISODateToString,
    msToMinutesString,
} from '../../../../utils/time';
import { findLatestError } from '../../../../utils/ErrorHandling/findLatestError';
import { Participant } from '../../../../typings/participant';
import {
    useGetParticipantActivityQuery,
    useGetParticipantsEmailsInfoQuery,
} from '../../../../store/api/participants';
import ProgressSpinner from '../../../../components/ProgressSpinner/ProgressSpinner';
import CandidateReportStatus from '../CandidateReportStatus/CandidateReportStatus';

interface ICandidateInfoModal {
    closeCandidateDetailsModal: () => void;
    participant: Participant;
    companyId: string;
    groupId: string;
    groupType?: string;
    invitationId: string;
    isSuperAdmin: boolean;
}
interface IActivityStatus {
    statusColor: 'red' | 'yellow' | 'green' | 'gray';
    label: string;
}

const ActivityStatus = (props: IActivityStatus) => {
    return (
        <Status>
            {props.statusColor === 'green' && <div className={'green'} />}
            {props.statusColor === 'yellow' && <div className={'yellow'} />}
            {props.statusColor === 'red' && <div className={'red'} />}
            {props.statusColor === 'gray' && <div className={'gray'} />}
            <span>{props.label}</span>
        </Status>
    );
};

const CandidateInfoModal: FunctionComponent<ICandidateInfoModal> = (props) => {
    const {
        closeCandidateDetailsModal,
        companyId,
        groupId,
        participant,
        invitationId,
        isSuperAdmin,
        groupType,
    } = props;

    const { data: participantActivity, isFetching: isFetchingActivity } =
        useGetParticipantActivityQuery({
            companyIdentifier: companyId,
            groupId: groupId,
            userId: participant.user_id,
            includeReportUrl: false,
        });

    const { data: emailsInfo, isFetching: isFetchingEmailsInfo } =
        useGetParticipantsEmailsInfoQuery(
            {
                companyIdentifier: companyId,
                groupId: groupId,
                userId: participant.user_id,
            },
            {
                skip: !participant.email && groupType === 'MOTIVIEW',
            },
        );

    useEffect(() => {
        const close = (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                closeCandidateDetailsModal();
            }
        };
        window.addEventListener('keydown', close);
        return () => window.removeEventListener('keydown', close);
    }, [closeCandidateDetailsModal]);

    const lastCompletedSession = getLastCompletedSession(participantActivity);

    const session = getLastCompletedSession(participantActivity);

    const hasErrorStatus = Boolean(
        participantActivity?.invitation?.status &&
            Object.keys(participantActivity.invitation.status).length,
    );

    const analysisStatus = participantActivity?.analysis?.status;
    const hasStarted =
        (participantActivity?.sessions?.length ?? 0) > 0 ||
        (participantActivity?.files?.uploaded_videos ?? 'none') !== 'none' ||
        (participantActivity?.files?.uploaded_json ?? 'none') !== 'none';

    const latestErrorMessage = findLatestError(
        participantActivity?.invitation?.status,
    );

    const isAnonymousMotiview = !participant.email && groupType === 'MOTIVIEW';

    const setPasswordEmail = emailsInfo?.find(
        (email) => email.template === 'UserSetPassword',
    );

    const invitationEmail = emailsInfo?.find(
        (email) => email.template === 'UserInvitation',
    );

    const reminderEmail = emailsInfo?.find(
        (email) => email.template === 'UserReminder',
    );

    const scanCompletionEmail = emailsInfo?.find(
        (email) => email.template === 'ScanCompletion',
    );

    const reportEmail = emailsInfo?.find(
        (email) => email.template === 'UserReport',
    );

    return (
        <CandidateInfoModalWrapper
            className={'candidate-info-pop-up'}
            onClick={closeCandidateDetailsModal}>
            <CandidateInfoSection onClick={(e) => e.stopPropagation()}>
                <CloseIcon>
                    <CrossIcon
                        style={{ cursor: 'pointer' }}
                        onClick={closeCandidateDetailsModal}
                        size={24}
                    />
                </CloseIcon>
                <CandidateInfoTitleSection className={'main-section'}>
                    <CandidateInfoTitle className={'main'}>
                        Scan and {isSuperAdmin ? 'analysis' : 'candidate'}{' '}
                        details
                        {isAnonymousMotiview && (
                            <span
                                style={{
                                    marginLeft: 8,
                                    padding: '2px 8px',
                                    borderRadius: 4,
                                    backgroundColor: '#e7f8f8',
                                    color: '#1AA277',
                                    fontSize: 12,
                                    fontWeight: 500,
                                    verticalAlign: 'middle',
                                }}>
                                Motiview user
                            </span>
                        )}
                    </CandidateInfoTitle>
                </CandidateInfoTitleSection>
                <SectionWrapper className={'main-section'}>
                    {!isAnonymousMotiview && (
                        <DetailWrapper>
                            <Detail className={'name'}>Name:</Detail>
                            <Detail className={'status-wrapper'}>
                                {participant.first_name
                                    ? participant.first_name
                                    : groupType === 'MOTIVIEW'
                                    ? 'Motiview user'
                                    : '-'}{' '}
                                {participant.last_name}
                            </Detail>
                        </DetailWrapper>
                    )}
                    <CandidateInfoTitleSection>
                        <CandidateInfoTitle className={'secondary-title'}>
                            Scan details
                        </CandidateInfoTitle>
                    </CandidateInfoTitleSection>
                    <DetailWrapper>
                        <Detail className={'name status-name'}>Status:</Detail>
                        <Detail className={'status-desc-wrapper'}>
                            {!!participantActivity && (
                                <Detail className={'status-wrapper'}>
                                    <CandidateReportStatus
                                        report={participantActivity.report}
                                        analysis={participantActivity.analysis}
                                        hasErrorStatus={hasErrorStatus}
                                        participantEmail={participant.email}
                                        groupType={groupType}
                                        hasStarted={hasStarted}
                                    />
                                </Detail>
                            )}
                            {!!lastCompletedSession &&
                                !participantActivity?.report &&
                                analysisStatus === 'pending' &&
                                hasErrorStatus && (
                                    <Detail className={'status-wrapper desc'}>
                                        {!!latestErrorMessage && isSuperAdmin
                                            ? latestErrorMessage.adminMessage
                                            : !!latestErrorMessage &&
                                              !isSuperAdmin
                                            ? latestErrorMessage.clientMessage
                                            : 'Something went wrong in the analysis. Please reach out to support. Apologies for the inconvenience.'}
                                    </Detail>
                                )}
                        </Detail>
                    </DetailWrapper>
                    {!isAnonymousMotiview && (
                        <CandidateInfoTitleSection>
                            <CandidateInfoTitle className={'secondary-title'}>
                                Email delivery info
                            </CandidateInfoTitle>
                        </CandidateInfoTitleSection>
                    )}
                    {!isAnonymousMotiview && isFetchingEmailsInfo && (
                        <ProgressSpinner />
                    )}
                    {!isAnonymousMotiview && !isFetchingEmailsInfo && (
                        <>
                            <DetailWrapper>
                                <Detail className={'name'}>Set password</Detail>
                                <Detail>
                                    {!setPasswordEmail
                                        ? '-'
                                        : setPasswordEmail?.delivered
                                        ? `delivered at ${setPasswordEmail?.delivered_at}`
                                        : setPasswordEmail?.bounced
                                        ? 'bounced'
                                        : `sent at ${setPasswordEmail?.sent_at}`}
                                </Detail>
                            </DetailWrapper>
                            <DetailWrapper>
                                <Detail className={'name'}>Invitation</Detail>
                                <Detail>
                                    {!invitationEmail
                                        ? '-'
                                        : invitationEmail?.delivered
                                        ? `delivered at ${invitationEmail?.delivered_at}`
                                        : invitationEmail?.bounced
                                        ? 'bounced'
                                        : `sent at ${invitationEmail?.sent_at}`}
                                </Detail>
                            </DetailWrapper>
                            <DetailWrapper>
                                <Detail className={'name'}>Reminder</Detail>
                                <Detail>
                                    {!reminderEmail
                                        ? '-'
                                        : reminderEmail?.delivered
                                        ? `delivered at ${reminderEmail?.delivered_at}`
                                        : reminderEmail?.bounced
                                        ? 'bounced'
                                        : `sent at ${reminderEmail?.sent_at}`}
                                </Detail>
                            </DetailWrapper>
                            <DetailWrapper>
                                <Detail className={'name'}>
                                    Scan completion
                                </Detail>
                                <Detail>
                                    {!scanCompletionEmail
                                        ? '-'
                                        : scanCompletionEmail?.delivered
                                        ? `delivered at ${scanCompletionEmail?.delivered_at}`
                                        : scanCompletionEmail?.bounced
                                        ? 'bounced'
                                        : `sent at ${scanCompletionEmail?.sent_at}`}
                                </Detail>
                            </DetailWrapper>
                            <DetailWrapper>
                                <Detail className={'name'}>Report</Detail>
                                <Detail>
                                    {!reportEmail
                                        ? '-'
                                        : reportEmail?.delivered
                                        ? `delivered at ${reportEmail?.delivered_at}`
                                        : reportEmail?.bounced
                                        ? 'bounced'
                                        : `sent at ${reportEmail?.sent_at}`}
                                </Detail>
                            </DetailWrapper>
                        </>
                    )}
                    {isSuperAdmin && (
                        <>
                            <CandidateInfoTitleSection>
                                <CandidateInfoTitle
                                    className={'secondary-title'}>
                                    File status
                                </CandidateInfoTitle>
                            </CandidateInfoTitleSection>
                            {(isFetchingActivity ||
                                !participantActivity?.files) && (
                                <ProgressSpinner />
                            )}
                            {!isFetchingActivity &&
                                !!participantActivity?.files && (
                                    <>
                                        <DetailWrapper>
                                            <Detail className={'name'}>
                                                Questionnaires uploaded:
                                            </Detail>
                                            <Detail>
                                                {participantActivity.files
                                                    .uploaded_json ===
                                                    'all' && (
                                                    <ActivityStatus
                                                        statusColor="green"
                                                        label="Successfull"
                                                    />
                                                )}
                                                {participantActivity.files
                                                    .uploaded_json ===
                                                    'some' && (
                                                    <ActivityStatus
                                                        statusColor="yellow"
                                                        label="Some"
                                                    />
                                                )}
                                                {participantActivity.files
                                                    .uploaded_json ===
                                                    'none' && (
                                                    <ActivityStatus
                                                        statusColor="gray"
                                                        label="None"
                                                    />
                                                )}
                                            </Detail>
                                        </DetailWrapper>
                                        <DetailWrapper>
                                            <Detail className={'name'}>
                                                Videos uploaded:
                                            </Detail>
                                            <Detail>
                                                {participantActivity.files
                                                    .uploaded_videos ===
                                                    'all' && (
                                                    <ActivityStatus
                                                        statusColor="green"
                                                        label="Successfull"
                                                    />
                                                )}
                                                {participantActivity.files
                                                    .uploaded_videos ===
                                                    'some' && (
                                                    <ActivityStatus
                                                        statusColor="yellow"
                                                        label="Some"
                                                    />
                                                )}
                                                {participantActivity.files
                                                    .uploaded_videos ===
                                                    'none' && (
                                                    <ActivityStatus
                                                        statusColor="gray"
                                                        label="None"
                                                    />
                                                )}
                                            </Detail>
                                        </DetailWrapper>
                                        <DetailWrapper>
                                            <Detail className={'name'}>
                                                Videos converted:
                                            </Detail>
                                            <Detail>
                                                {participantActivity.files
                                                    .videos_converted ===
                                                    'all' && (
                                                    <ActivityStatus
                                                        statusColor="green"
                                                        label="Successfull"
                                                    />
                                                )}
                                                {participantActivity.files
                                                    .videos_converted ===
                                                    'some' && (
                                                    <ActivityStatus
                                                        statusColor="yellow"
                                                        label="Some"
                                                    />
                                                )}
                                                {participantActivity.files
                                                    .videos_converted ===
                                                    'none' && (
                                                    <ActivityStatus
                                                        statusColor="gray"
                                                        label="None"
                                                    />
                                                )}
                                            </Detail>
                                        </DetailWrapper>
                                    </>
                                )}
                        </>
                    )}
                    {isSuperAdmin && (
                        <>
                            <CandidateInfoTitleSection>
                                <CandidateInfoTitle
                                    className={'secondary-title'}>
                                    Session
                                </CandidateInfoTitle>
                            </CandidateInfoTitleSection>
                            <DetailWrapper>
                                <Detail className={'name'}>Browser:</Detail>
                                <Detail>
                                    {!!session
                                        ? session.browser.name +
                                          ' ' +
                                          session.browser.version
                                        : '-'}
                                </Detail>
                            </DetailWrapper>
                            <DetailWrapper>
                                <Detail className={'name'}>OS:</Detail>
                                <Detail>
                                    {!!session ? session.device.os.name : '-'}
                                </Detail>
                            </DetailWrapper>
                        </>
                    )}
                    <DetailWrapper>
                        <Detail className={'name'}>Log-in at:</Detail>
                        <Detail>
                            {!!session && !!session.loggedInAt
                                ? ISODateToString(session.loggedInAt)
                                : '-'}
                        </Detail>
                    </DetailWrapper>
                    <DetailWrapper>
                        <Detail className={'name'}>Finished at:</Detail>
                        <Detail>
                            {!!session && !!session.finishedAt
                                ? ISODateToString(session.finishedAt)
                                : '-'}
                        </Detail>
                    </DetailWrapper>
                    <DetailWrapper>
                        <Detail className={'name'}>Total time:</Detail>
                        <Detail>
                            {!!session && !!session.totalTime
                                ? msToMinutesString(session.totalTime)
                                : '-'}
                        </Detail>
                    </DetailWrapper>
                </SectionWrapper>
                <hr />
                <SectionWrapper className={'info'}>
                    <CandidateInfoTitleSection>
                        <CandidateInfoTitle className={'secondary-title'}>
                            Candidate details
                        </CandidateInfoTitle>
                    </CandidateInfoTitleSection>
                    <DetailWrapper>
                        <Detail className={'name'}>Registered at:</Detail>
                        <Detail>
                            {formatDate(participant.user_registered_at)}
                        </Detail>
                    </DetailWrapper>
                    {!isAnonymousMotiview && (
                        <DetailWrapper>
                            <Detail className={'name'}>Invited at:</Detail>
                            <Detail>
                                {formatDate(participant.invited_at)}
                            </Detail>
                        </DetailWrapper>
                    )}
                    {!isAnonymousMotiview && (
                        <DetailWrapper>
                            <Detail className={'name'}>E-mail:</Detail>
                            <Detail>{participant.email ?? '-'}</Detail>
                        </DetailWrapper>
                    )}
                    <DetailWrapper>
                        <Detail className={'name'}>Candidate ID:</Detail>
                        <Detail>{participant.user_id ?? '-'}</Detail>
                    </DetailWrapper>
                    {isSuperAdmin && (
                        <DetailWrapper>
                            <Detail className={'name'}>Invitation ID:</Detail>
                            <Detail>{invitationId}</Detail>
                        </DetailWrapper>
                    )}
                </SectionWrapper>
            </CandidateInfoSection>
        </CandidateInfoModalWrapper>
    );
};
export default CandidateInfoModal;
